/**
 * Adder-mapping service for SubContractorHub (Story SCH-2.3).
 *
 * Wraps the discover-adders branch + five adder-mapping endpoints.
 * Mirrors stageMappingService.ts conventions: shared fetchJson helper,
 * snake_case ↔ camelCase translation at the boundary, discriminated
 * union result types so callers don't have to throw.
 */

import { supabase } from '@/lib/supabaseClient';
import type {
  AdderCandidate,
  AdderMappingRow,
  CreateAdderAndMappingRequest,
  CreateAdderAndMappingResult,
  CreateAdderMappingRequest,
  CreateAdderMappingResult,
  CreatedAdderRow,
  DeleteAdderMappingResult,
  DiscoverAddersResponse,
  DiscoverAddersResult,
  ListAdderMappingsResult,
  UpdateAdderMappingRequest,
  UpdateAdderMappingResult,
} from '@/types/integrations/adderMapping';

const BACKEND_URL =
  import.meta.env.VITE_PYTHON_BACKEND_URL || 'http://localhost:8000';

async function authHeader(): Promise<HeadersInit> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }
  return {
    Authorization: `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  };
}

interface ApiEnvelope<TData> {
  data: TData | null;
  error: { code: string; message: string; details?: Record<string, unknown> } | null;
  meta: unknown;
}

async function fetchJson<TData>(
  path: string,
  init: RequestInit,
): Promise<ApiEnvelope<TData>> {
  const headers = await authHeader();
  const response = await fetch(`${BACKEND_URL}${path}`, {
    ...init,
    headers: { ...headers, ...(init.headers ?? {}) },
  });
  if (!response.ok && response.status !== 200) {
    let detail: string | undefined;
    try {
      const json = await response.json();
      detail = typeof json?.detail === 'string' ? json.detail : undefined;
    } catch {
      // ignore parse error
    }
    throw new Error(detail || `Request failed with status ${response.status}`);
  }
  return (await response.json()) as ApiEnvelope<TData>;
}

// ─── Raw → camelCase mappers ──────────────────────────────────────────────
interface RawAdderCandidate {
  id: number;
  name: string;
  category: string | null;
  pricing_model: 'quote' | 'fixed' | 'per_unit' | 'per_watt' | 'per_foot';
  unit_price: number | null;
  unit_label: string | null;
}

function _mapAdderCandidate(raw: RawAdderCandidate): AdderCandidate {
  return {
    id: raw.id,
    name: raw.name,
    category: raw.category,
    pricingModel: raw.pricing_model,
    unitPrice: raw.unit_price ?? null,
    unitLabel: raw.unit_label ?? null,
  };
}

interface RawAdderMappingRow {
  id: string;
  tenant_integration_id: string;
  external_adder_name: string;
  joules_adder_id: number | null;
  joules_adder_name: string | null;
  enabled: boolean;
  created_at: string | null;
  updated_at: string | null;
}

function _mapAdderMappingRow(raw: RawAdderMappingRow): AdderMappingRow {
  return {
    id: raw.id,
    tenantIntegrationId: raw.tenant_integration_id,
    externalAdderName: raw.external_adder_name,
    joulesAdderId: raw.joules_adder_id,
    joulesAdderName: raw.joules_adder_name,
    enabled: raw.enabled,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

interface RawCreatedAdderRow {
  id: number;
  tenant_id: string | null;
  name: string;
  category: string | null;
  pricing_model: 'quote' | 'fixed' | 'per_unit' | 'per_watt' | 'per_foot';
  unit_price: number | null;
  unit_label: string | null;
  is_active: boolean;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

function _mapCreatedAdderRow(raw: RawCreatedAdderRow): CreatedAdderRow {
  return {
    id: raw.id,
    tenantId: raw.tenant_id,
    name: raw.name,
    category: raw.category,
    pricingModel: raw.pricing_model,
    unitPrice: raw.unit_price ?? null,
    unitLabel: raw.unit_label ?? null,
    isActive: raw.is_active,
    createdBy: raw.created_by,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

const DISCOVER_ERROR_CODES = [
  'integration_not_found',
  'auth_failed',
  'connection_test_failed',
  'internal_error',
] as const;
const MAPPING_ERROR_CODES = ['integration_not_found', 'internal_error'] as const;

function _normalizeError<TCode extends string>(
  raw: { code: string; message: string; details?: Record<string, unknown> } | null,
  knownCodes: readonly TCode[],
): { code: TCode; message: string; details?: Record<string, unknown> } {
  if (!raw) {
    return { code: 'internal_error' as TCode, message: 'Unknown error' };
  }
  const code = (knownCodes as readonly string[]).includes(raw.code)
    ? (raw.code as TCode)
    : ('internal_error' as TCode);
  return { code, message: raw.message, details: raw.details };
}

// ─── Raw discover-adders shape ───────────────────────────────────────────
interface RawAutoMatchResult {
  joules_target_id: number;
  confidence: number;
}

interface RawDiscoverAddersResponse {
  sch_adder_names: string[];
  joules_adders: RawAdderCandidate[];
  suggestions: Record<string, RawAutoMatchResult | null>;
  existing_mappings: RawAdderMappingRow[];
}

function _mapDiscoverAddersResponse(
  raw: RawDiscoverAddersResponse,
): DiscoverAddersResponse {
  return {
    schAdderNames: raw.sch_adder_names,
    joulesAdders: raw.joules_adders.map(_mapAdderCandidate),
    suggestions: Object.fromEntries(
      Object.entries(raw.suggestions).map(([name, suggestion]) => [
        name,
        suggestion
          ? {
              joulesTargetId: suggestion.joules_target_id,
              confidence: suggestion.confidence,
            }
          : null,
      ]),
    ),
    existingMappings: raw.existing_mappings.map(_mapAdderMappingRow),
  };
}

export const adderMappingService = {
  // ─── GET /discover-catalog?include=adders ──────────────────────────────
  async discoverAdders(): Promise<DiscoverAddersResult> {
    const env = await fetchJson<RawDiscoverAddersResponse>(
      '/api/v1/integrations/subcontractorhub/discover-catalog?include=adders',
      { method: 'GET' },
    );
    if (env.data) {
      return { data: _mapDiscoverAddersResponse(env.data), error: null };
    }
    return { data: null, error: _normalizeError(env.error, DISCOVER_ERROR_CODES) };
  },

  // ─── GET /adder-mappings ────────────────────────────────────────────────
  async listAdderMappings(): Promise<ListAdderMappingsResult> {
    const env = await fetchJson<{ rows: RawAdderMappingRow[] }>(
      '/api/v1/integrations/subcontractorhub/adder-mappings',
      { method: 'GET' },
    );
    if (env.data) {
      return {
        data: { rows: env.data.rows.map(_mapAdderMappingRow) },
        error: null,
      };
    }
    return { data: null, error: _normalizeError(env.error, MAPPING_ERROR_CODES) };
  },

  // ─── POST /adder-mappings (UPSERT) ──────────────────────────────────────
  async createAdderMapping(
    req: CreateAdderMappingRequest,
  ): Promise<CreateAdderMappingResult> {
    const body: Record<string, unknown> = {
      external_adder_name: req.externalAdderName,
      joules_adder_id: req.joulesAdderId,
    };
    if (req.enabled !== undefined) body.enabled = req.enabled;

    const env = await fetchJson<RawAdderMappingRow>(
      '/api/v1/integrations/subcontractorhub/adder-mappings',
      { method: 'POST', body: JSON.stringify(body) },
    );
    if (env.data) {
      return { data: _mapAdderMappingRow(env.data), error: null };
    }
    return { data: null, error: _normalizeError(env.error, MAPPING_ERROR_CODES) };
  },

  // ─── POST /adder-mappings/with-new-adder ────────────────────────────────
  async createAdderWithMapping(
    req: CreateAdderAndMappingRequest,
  ): Promise<CreateAdderAndMappingResult> {
    const body: Record<string, unknown> = {
      external_adder_name: req.externalAdderName,
      adder: {
        name: req.adder.name,
        category: req.adder.category ?? null,
        pricing_model: req.adder.pricingModel,
        unit_price: req.adder.unitPrice ?? null,
        unit_label: req.adder.unitLabel ?? null,
        is_active: req.adder.isActive ?? true,
      },
    };

    const env = await fetchJson<{
      adder: RawCreatedAdderRow;
      mapping: RawAdderMappingRow;
    }>('/api/v1/integrations/subcontractorhub/adder-mappings/with-new-adder', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (env.data) {
      return {
        data: {
          adder: _mapCreatedAdderRow(env.data.adder),
          mapping: _mapAdderMappingRow(env.data.mapping),
        },
        error: null,
      };
    }
    return { data: null, error: _normalizeError(env.error, MAPPING_ERROR_CODES) };
  },

  // ─── PATCH /adder-mappings/{id} ─────────────────────────────────────────
  async updateAdderMapping(
    id: string,
    req: UpdateAdderMappingRequest,
  ): Promise<UpdateAdderMappingResult> {
    const body: Record<string, unknown> = {};
    if (req.joulesAdderId !== undefined) body.joules_adder_id = req.joulesAdderId;
    if (req.enabled !== undefined) body.enabled = req.enabled;

    const env = await fetchJson<RawAdderMappingRow>(
      `/api/v1/integrations/subcontractorhub/adder-mappings/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    );
    if (env.data) {
      return { data: _mapAdderMappingRow(env.data), error: null };
    }
    return { data: null, error: _normalizeError(env.error, MAPPING_ERROR_CODES) };
  },

  // ─── DELETE /adder-mappings/{id} ────────────────────────────────────────
  async deleteAdderMapping(id: string): Promise<DeleteAdderMappingResult> {
    const env = await fetchJson<{ deleted_id: string }>(
      `/api/v1/integrations/subcontractorhub/adder-mappings/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    );
    if (env.data) {
      return { data: { deletedId: env.data.deleted_id }, error: null };
    }
    return { data: null, error: _normalizeError(env.error, MAPPING_ERROR_CODES) };
  },
};
